import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  DocumentCheckIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@components/ui';
import { trackingHelpers } from '@/services/mixpanel';

interface CertificateProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export function CertificateProcessModal({
  isOpen,
  onClose,
  onComplete,
}: CertificateProcessModalProps) {
  const [step, setStep] = useState<1 | 2>(1);

  // Reset to step 1 when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🔄 CertificateProcessModal opened - Resetting to step 1');
      setStep(1);
      // Track modal opened
      trackingHelpers.trackCertificateModal.opened('invoice-wizard');
    }
  }, [isOpen]);

  const handleNext = () => {
    console.log('➡️ Moving from step 1 to step 2');
    // Track guide step viewed
    trackingHelpers.trackCertificateModal.guideStepViewed(2, 'Completar formulario');
    setStep(2);
  };

  const handleOpenForm = () => {
    console.log('🌐 Opening certificate form in new tab');
    // Track link clicked
    trackingHelpers.trackCertificateModal.linkClicked(
      'certificate_form',
      'https://certificados.facturalibre.io/',
      step
    );
    // Open the certificate form in a new tab
    window.open('https://certificados.facturalibre.io/', '_blank');
  };

  const handleClose = () => {
    console.log('❌ Closing modal and resetting to step 1');
    setStep(1); // Reset to step 1
    onClose();
  };

  const handleFinish = () => {
    console.log('✅ User clicked "Entendido, lo haré después" - Completing process');
    // Track guide completed
    trackingHelpers.trackCertificateModal.guideCompleted('postponed');
    setStep(1); // Reset to step 1
    onComplete();
  };

  // Log current step on each render
  console.log('📍 CertificateProcessModal render - Current step:', step, 'isOpen:', isOpen);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-secondary-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-full">
                      <DocumentCheckIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-secondary-900 dark:text-white"
                      >
                        {step === 1
                          ? 'Sigue las siguientes instrucciones para obtener tu certificado'
                          : 'Completa el formulario de solicitud y realiza el pago del trámite'}
                      </Dialog.Title>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Step 1: Information about the process */}
                {step === 1 && (
                  <>
                    {/* Subtitle */}
                    <div className="mb-6">
                      <p className="text-sm text-secondary-600 dark:text-secondary-400">
                        El trámite del certificado de firma electrónica tiene un costo de{' '}
                        <span className="font-semibold text-secondary-900 dark:text-white">$119.99</span>.
                        Nosotros nos encargaremos de ayudarte en todo el proceso.
                      </p>
                    </div>

                    {/* Benefits/Features */}
                    <div className="mb-6 space-y-3">
                      <div className="flex items-start gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg
                            className="h-5 w-5 text-primary-600 dark:text-primary-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="text-sm">
                          <p className="font-medium text-primary-900 dark:text-primary-100">
                            Proceso guiado paso a paso
                          </p>
                          <p className="text-primary-700 dark:text-primary-300">
                            Te ayudamos con todo el trámite ante la DGI
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg
                            className="h-5 w-5 text-primary-600 dark:text-primary-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="text-sm">
                          <p className="font-medium text-primary-900 dark:text-primary-100">
                            Soporte dedicado
                          </p>
                          <p className="text-primary-700 dark:text-primary-300">
                            Nuestro equipo está disponible para resolver tus dudas
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                        <div className="flex-shrink-0 mt-0.5">
                          <ClockIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="text-sm">
                          <p className="font-medium text-primary-900 dark:text-primary-100">
                            Procesamiento rápido
                          </p>
                          <p className="text-primary-700 dark:text-primary-300">
                            Tiempo de procesamiento: 5 a 10 días hábiles
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full justify-center"
                      onClick={handleNext}
                      leftIcon={<ArrowRightIcon className="h-5 w-5" />}
                    >
                      Siguiente
                    </Button>
                  </>
                )}

                {/* Step 2: Form and payment instructions */}
                {step === 2 && (
                  <>
                    {/* Subtitle */}
                    <div className="mb-6">
                      <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                        Llena el formulario dando clic en el botón. Una vez hayas completado el proceso,
                        en 5 a 10 días hábiles nuestro equipo procesará tu solicitud.
                      </p>
                      <div className="p-4 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
                        <p className="text-sm text-warning-800 dark:text-warning-200 font-medium">
                          ⚠️ Importante: Debes estar atento al correo electrónico que estás utilizando para recibir
                          actualizaciones sobre tu solicitud.
                        </p>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="mb-6 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary-600 dark:bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          1
                        </div>
                        <p className="text-sm text-secondary-700 dark:text-secondary-300 pt-0.5">
                          Haz clic en el botón "Abrir Formulario" para ir al sitio de registro
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary-600 dark:bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          2
                        </div>
                        <p className="text-sm text-secondary-700 dark:text-secondary-300 pt-0.5">
                          Completa el formulario con tus datos y realiza el pago de $119.99
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary-600 dark:bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          3
                        </div>
                        <p className="text-sm text-secondary-700 dark:text-secondary-300 pt-0.5">
                          Espera la confirmación en tu correo electrónico (5 a 10 días hábiles)
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <Button
                        variant="primary"
                        size="lg"
                        className="w-full justify-center"
                        onClick={handleOpenForm}
                        leftIcon={<ArrowTopRightOnSquareIcon className="h-5 w-5" />}
                      >
                        Abrir Formulario
                      </Button>

                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full justify-center"
                        onClick={handleFinish}
                      >
                        Entendido, lo haré después
                      </Button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
